const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const behaviour = () => reducedMotion() ? 'instant' : 'smooth';

class SanjarGallery extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.track = this.querySelector('[data-sl-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    if (this.track.matches('.sl-gallery-track')) {
      const warmPhotos = () => {
        this.slides.slice(0, 2).forEach(slide => {
          const image = slide.querySelector('img');
          if (image) image.loading = 'eager';
        });
      };
      this.track.addEventListener('pointerenter', warmPhotos, { signal, once: true });
      this.track.addEventListener('focusin', warmPhotos, { signal, once: true });
      if ('IntersectionObserver' in window) {
        this.nearbyObserver = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          warmPhotos();
          this.nearbyObserver.disconnect();
        }, { rootMargin: '900px 0px' });
        this.nearbyObserver.observe(this);
      }
    }
    this.querySelector('[data-sl-prev]')?.addEventListener('click', () => this.move(-1), { signal });
    this.querySelector('[data-sl-next]')?.addEventListener('click', () => this.move(1), { signal });
    this.querySelectorAll('[data-sl-dot]').forEach(dot => dot.addEventListener('click', () => this.go(Number(dot.dataset.slDot)), { signal }));
    this.track.addEventListener('pointerdown', event => {
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }, { signal, passive: true });
    this.track.addEventListener('pointermove', event => {
      if (!this.pointerStart) return;
      const dx = Math.abs(event.clientX - this.pointerStart.x);
      const dy = Math.abs(event.clientY - this.pointerStart.y);
      if (dx > 8 && dx > dy) {
        this.interacted = true;
        this.hydrateAround(this.index);
        this.pointerStart = null;
      }
    }, { signal, passive: true });
    ['pointerup', 'pointercancel'].forEach(type => this.track.addEventListener(type, () => {
      this.pointerStart = null;
    }, { signal, passive: true }));
    this.track.addEventListener('scroll', () => this.scheduleUpdate(), { signal, passive: true });
    this.track.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home' || event.key === 'End') this.go(event.key === 'Home' ? 0 : this.slides.length - 1);
      else this.move(event.key === 'ArrowLeft' ? -1 : 1);
    }, { signal });
    this.resize = new ResizeObserver(() => this.scheduleUpdate());
    this.resize.observe(this.track);
    this.update();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.resize?.disconnect();
    this.nearbyObserver?.disconnect();
    cancelAnimationFrame(this.updateFrame);
  }
  scheduleUpdate() {
    if (this.updateFrame) return;
    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = 0;
      this.update();
    });
  }
  hydrate(index) {
    const deferred = this.slides[index]?.querySelector('template[data-sl-card-image]');
    if (deferred) {
      const content = deferred.content.cloneNode(true);
      const image = content.querySelector('img');
      const sizes = this.slides[0]?.querySelector('img')?.sizes;
      if (image && sizes) image.sizes = sizes;
      deferred.replaceWith(content);
    }
  }
  hydrateAround(index) {
    [index - 1, index, index + 1].forEach(position => this.hydrate(position));
  }
  get index() {
    const left = this.track.getBoundingClientRect().left;
    let best = 0, distance = Infinity;
    this.slides.forEach((slide, index) => {
      const delta = Math.abs(slide.getBoundingClientRect().left - left);
      if (delta < distance) { distance = delta; best = index; }
    });
    return best;
  }
  go(index) {
    const position = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[position];
    if (!slide) return;
    this.interacted = true;
    this.hydrateAround(position);
    this.track.scrollBy({ left: slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: behaviour() });
  }
  move(direction) { this.go(this.index + direction); }
  update() {
    const current = this.index;
    if (this.track.scrollLeft > 2) this.interacted = true;
    if (this.interacted) this.hydrateAround(current);
    const previous = this.querySelector('[data-sl-prev]'), next = this.querySelector('[data-sl-next]');
    if (previous) previous.disabled = this.track.scrollLeft <= 2;
    if (next) next.disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    this.querySelectorAll('[data-sl-dot]').forEach((dot, index) => { if (index === current) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
    const count = this.querySelector('[data-sl-count]');
    if (count) count.textContent = this.slides.length ? `${current + 1} / ${this.slides.length}` : '';
  }
}

class SanjarTabs extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.tabs = [...this.querySelectorAll('[data-sl-tab]')];
    this.panels = [...this.querySelectorAll('[data-sl-panel]')];
    this.querySelector('[data-sl-tablist]')?.setAttribute('role', 'tablist');
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', this.panels[index]?.id || '');
      tab.addEventListener('click', event => { event.preventDefault(); this.activate(index); }, { signal });
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault(); this.activate(next); this.tabs[next].focus();
      }, { signal });
    });
    this.panels.forEach(panel => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '0');
      panel.dataset.shown = this.dataset.initial || '24';
      panel.querySelector('[data-sl-more]')?.addEventListener('click', () => {
        panel.dataset.shown = String(Number(panel.dataset.shown) + Number(this.dataset.step || 4));
        this.filter();
      }, { signal });
    });
    this.querySelectorAll('[data-sl-category]').forEach(button => button.addEventListener('click', () => {
      const owner=button.dataset.slOwner, category=button.dataset.slCategory;
      this.querySelectorAll('[data-sl-category]').forEach(b => {if(b.dataset.slOwner===owner)b.setAttribute('aria-pressed',String(b===button));});
      this.querySelectorAll('[data-sl-category-grid]').forEach(grid=>{if(grid.dataset.slOwner!==owner)return;grid.hidden=grid.dataset.slCategoryGrid!==category;if(!grid.hidden){const panel=grid.closest('[data-sl-panel]');panel.dataset.shown=this.dataset.initial||'4';const link=panel.querySelector('.sl-view-collection');if(link&&grid.dataset.slCollectionUrl)link.href=grid.dataset.slCollectionUrl;}});
      this.filter();
    },{signal}));
    const warmSelection = () => {
      if (this.querySelector('[data-sl-remote]') && !navigator.connection?.saveData) this.loadRemoteSection().catch(() => {});
    };
    this.querySelectorAll('[data-sl-tab], [data-sl-category]').forEach(control => {
      control.addEventListener('pointerenter', warmSelection, { signal, once: true });
      control.addEventListener('focusin', warmSelection, { signal, once: true });
      control.addEventListener('pointerdown', warmSelection, { signal, passive: true });
    });
    this.querySelector('[data-sl-search]')?.addEventListener('input', () => this.resetFilter(), { signal });
    this.querySelector('[data-sl-sort]')?.addEventListener('change', () => this.filter(), { signal });
    this.addEventListener('shopify:block:select', event => {
      const index = this.tabs.findIndex(tab => tab.getAttribute('data-shopify-editor-block')?.includes(event.detail.blockId));
      if (index >= 0) this.activate(index);
    }, { signal });
    this.activate(0);
  }
  disconnectedCallback() { this.abort?.abort(); }
  loadRemoteSection() {
    if (this.remoteSection) return this.remoteSection;
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', this.dataset.slSectionId);
    this.remoteSection = fetch(url, { signal: this.abort.signal }).then(response => {
      if (!response.ok) throw new Error('Selection unavailable');
      return response.text();
    }).then(html => new DOMParser().parseFromString(html, 'text/html')).catch(error => {
      this.remoteSection = null;
      throw error;
    });
    return this.remoteSection;
  }
  async loadGrid(grid) {
    const section = await this.loadRemoteSection();
    if (!this.isConnected || !grid.hasAttribute('data-sl-remote')) return;
    const source = section.getElementById(grid.id);
    if (!source || source.hasAttribute('data-sl-remote')) {
      this.remoteSection = null;
      throw new Error('Selection unavailable');
    }
    grid.replaceChildren(...[...source.childNodes].map(node => node.cloneNode(true)));
    grid.removeAttribute('data-sl-remote');
  }
  activate(index) {
    this.tabs.forEach((tab, position) => {
      tab.setAttribute('aria-selected', String(position === index));
      tab.tabIndex = position === index ? 0 : -1;
    });
    this.panels.forEach((panel, position) => { panel.hidden = position !== index; });
    this.querySelectorAll("[data-sl-subtabs]").forEach((row, position) => { row.hidden = position !== index || !row.children.length; });
    this.filter();
  }
  resetFilter() {
    this.panels.forEach(panel => { panel.dataset.shown = this.dataset.initial || '24'; });
    this.filter();
  }
  async filter() {
    const version = this.filterVersion = (this.filterVersion || 0) + 1;
    const query = this.querySelector('[data-sl-search]')?.value.trim().toLocaleLowerCase() || '';
    const sort = this.querySelector('[data-sl-sort]')?.value || 'featured';
    for (const panel of this.panels) {
      if (panel.hidden) continue;
      const grid = panel.querySelector('[data-sl-grid]:not([hidden])');
      if (!grid) continue;
      const status = panel.querySelector('[data-sl-loading]');
      if (grid.hasAttribute('data-sl-remote')) {
        grid.setAttribute('aria-busy', 'true');
        if (status) { status.hidden = false; status.textContent = this.dataset.slLoadingLabel; }
        panel.querySelector('[data-sl-no-results]').hidden = true;
        try {
          await this.loadGrid(grid);
        } catch (error) {
          if (version !== this.filterVersion || !this.isConnected) return;
          grid.removeAttribute('aria-busy');
          if (status) { status.hidden = false; status.textContent = this.dataset.slErrorLabel; }
          return;
        }
      }
      grid.removeAttribute('aria-busy');
      if (version !== this.filterVersion || panel.hidden || grid.hidden || !this.isConnected) return;
      if (status) status.hidden = true;
      const deferred = grid.querySelector("template[data-sl-deferred]");
      if (deferred) { deferred.replaceWith(deferred.content); }
      const items = [...grid.querySelectorAll('[data-sl-item]')];
      items.sort((a, b) => sort === 'price-asc' ? Number(a.dataset.price) - Number(b.dataset.price) : sort === 'price-desc' ? Number(b.dataset.price) - Number(a.dataset.price) : sort === 'title' ? a.dataset.title.localeCompare(b.dataset.title) : Number(a.dataset.order) - Number(b.dataset.order));
      const shown = Number(panel.dataset.shown); let matched = 0;
      items.forEach((item, index) => {
        if (grid.children[index] !== item) grid.insertBefore(item, grid.children[index] || null);
        const match = item.dataset.title.includes(query);
        item.hidden = !match || matched >= shown;
        if (match) matched++;
      });
      const more = panel.querySelector('[data-sl-more]');
      if (more) more.hidden = matched <= shown;
      const empty = panel.querySelector('[data-sl-no-results]');
      if (empty) empty.hidden = matched !== 0;
    }
  }
}

if (!customElements.get('sl-gallery')) customElements.define('sl-gallery', SanjarGallery);
if (!customElements.get('sl-tabs')) customElements.define('sl-tabs', SanjarTabs);

const ui = document.querySelector('[data-sl-shopping]');
if (ui) {
  const labels = JSON.parse(ui.querySelector('script[type="application/json"]').textContent);
  const root = window.Shopify?.routes?.root || '/';
  const key = type => `sanjar-luxe:${type}`;
  const read = type => {
    try { const value = JSON.parse(localStorage.getItem(key(type)) || '[]'); return Array.isArray(value) ? value.filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle)).slice(0, type === 'compare' ? 3 : 100) : []; }
    catch { return []; }
  };
  const live = ui.querySelector('[data-sl-live]'), toast = ui.querySelector('[data-sl-toast]'); let timer;
  const dismiss = () => { clearTimeout(timer); toast.hidden = true; live.textContent = ''; };
  ui.querySelector('[data-sl-toast-close]').addEventListener('click', dismiss);
  const announce = message => { live.textContent = message; toast.hidden = false; clearTimeout(timer); timer = setTimeout(dismiss, 4500); };
  const refresh = () => {
    ['save', 'compare'].forEach(type => {
      const handles = read(type);
      document.querySelectorAll(`[data-sl-${type}]`).forEach(button => button.setAttribute('aria-pressed', String(handles.includes(button.dataset[type === 'save' ? 'slSave' : 'slCompare']))));
      document.querySelectorAll(`[data-sl-${type}-count]`).forEach(node => { if (node.textContent !== String(handles.length)) node.textContent = String(handles.length); });
    });
  };
  const toggle = (type, handle) => {
    if (!/^[a-z0-9-]+$/.test(handle)) return;
    const handles = read(type), index = handles.indexOf(handle);
    if (index >= 0) handles.splice(index, 1);
    else if (type === 'compare' && handles.length >= 3) { announce(labels.compare_limit); return; }
    else if (type === 'save' && handles.length >= 100) { announce(labels.save_limit); return; }
    else handles.push(handle);
    try { localStorage.setItem(key(type), JSON.stringify(handles)); }
    catch { announce(labels.storage_error); return; }
    refresh(); announce(index >= 0 ? labels.removed : labels.saved);
  };
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: currency || labels.currency }).format(cents / 100);
  const node = (tag, text, className) => { const element = document.createElement(tag); if (text !== undefined) element.textContent = text; if (className) element.className = className; return element; };
  const productLink = (product, text) => { const link = node('a', text); link.href = `${root}products/${encodeURIComponent(product.handle)}`; return link; };
  const image = product => {
    if (!product.featured_image) return null;
    const url = new URL(product.featured_image, location.origin);
    if (!['https:', 'http:'].includes(url.protocol)) return null;
    const img = node('img'); url.searchParams.set('width', '480'); img.src = url.href; img.alt = product.title; img.loading = 'lazy'; return img;
  };
  let renderVersion = 0;
  const renderSaved = async type => {
    const version = ++renderVersion;
    const content = ui.querySelector('[data-sl-saved-content]');
    content.replaceChildren(node('p', labels.loading));
    const handles = read(type);
    const results = await Promise.allSettled(handles.map(async handle => {
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) throw new Error('unavailable');
      return response.json();
    }));
    if (version !== renderVersion) return;
    content.replaceChildren();
    if (!handles.length) { content.append(node('p', type === 'save' ? labels.empty_saved : labels.empty_compare, 'sl-empty')); return; }
    const products = results.filter(result => result.status === 'fulfilled').map(result => result.value);
    const failed = results.filter(result => result.status === 'rejected').length;
    if (failed) {
      content.append(node('p', labels.unavailable, 'sl-muted'));
      results.forEach((result, index) => {
        if (result.status !== 'rejected') return;
        const row = node('p'), remove = node('button', `${labels.remove}: ${handles[index]}`, 'sl-link');
        remove.type = 'button'; remove.addEventListener('click', () => { toggle(type, handles[index]); renderSaved(type); }); row.append(remove); content.append(row);
      });
    }
    const removeButton = product => { const button = node('button', labels.remove, 'sl-link'); button.type = 'button'; button.addEventListener('click', () => { toggle(type, product.handle); renderSaved(type); }); return button; };
    if (type === 'save') {
      const grid = node('div', undefined, 'sl-saved-grid');
      products.forEach(product => { const card = node('article', undefined, 'sl-saved-item'), link = productLink(product); const img = image(product); if (img) link.append(img); link.append(node('p', product.title)); card.append(link, node('p', money(product.price)), removeButton(product)); grid.append(card); });
      content.append(grid);
    } else {
      const scroll = node('div', undefined, 'sl-compare-scroll'), table = node('table', undefined, 'sl-compare-table'), header = node('tr');
      const blank = node('th', labels.product); blank.scope = 'col'; header.append(blank);
      products.forEach(product => { const cell = node('th'); cell.scope = 'col'; const link = productLink(product); const img = image(product); if (img) link.append(img); link.append(node('span', product.title)); cell.append(link, removeButton(product)); header.append(cell); });
      const thead = node('thead'); thead.append(header); table.append(thead);
      const tbody = node('tbody');
      const rows = [[labels.price, product => money(product.price)], [labels.availability, product => product.available ? labels.in_stock : labels.sold_out], [labels.type, product => product.type || '—'], [labels.options, product => product.options.map(option => `${option.name}: ${option.values.join(', ')}`).join(' · ')]];
      rows.forEach(([label, value]) => { const row = node('tr'), th = node('th', label); th.scope = 'row'; row.append(th); products.forEach(product => row.append(node('td', value(product)))); tbody.append(row); });
      table.append(tbody); scroll.append(table); content.append(scroll);
    }
  };
  let previousFocus;
  const dialog = ui.querySelector('dialog');
  dialog.addEventListener('close', () => { renderVersion++; previousFocus?.focus({ preventScroll: true }); });
  dialog.addEventListener('click', event => { if (event.target !== dialog) return; const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); });
  document.addEventListener('click', event => {
    const button = event.target.closest('button'); if (!button) return;
    if (button.dataset.slSave) toggle('save', button.dataset.slSave);
    if (button.dataset.slCompare) toggle('compare', button.dataset.slCompare);
    if (button.hasAttribute('data-sl-close')) dialog.close();
    if (button.dataset.slOpen) {
      const type = button.dataset.slOpen;
      const drawer = button.closest('header-drawer');
      previousFocus = drawer?.querySelector('summary') || button;
      const open = () => {
        ui.querySelector('[data-sl-dialog-title]').textContent = type === 'save' ? labels.wishlist : labels.compare;
        if (!dialog.open) dialog.showModal();
        renderSaved(type);
      };
      const details = drawer?.querySelector('details');
      if (details?.open && typeof drawer.close === 'function') {
        const closed = new MutationObserver(() => {
          if (details.open) return;
          closed.disconnect();
          open();
        });
        closed.observe(details, { attributes: true, attributeFilter: ['open'] });
        drawer.close();
      } else open();
    }
    if (button.dataset.slHotspot) {
      const preview = document.getElementById(button.dataset.slHotspot);
      if (preview) { previousFocus = button; preview.showModal(); }
    }
    if (button.hasAttribute('data-sl-look-close')) { const preview = button.closest('dialog'); preview?.close(); previousFocus?.focus({ preventScroll: true }); }
  });
  window.addEventListener('storage', refresh);
  document.addEventListener('shopify:section:load', refresh);
  let pending = false;
  const shoppingRoot = document.querySelector('main');
  if (shoppingRoot) new MutationObserver(records => {
    const addedControls = records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('[data-sl-save],[data-sl-compare]') || node.querySelector('[data-sl-save],[data-sl-compare]'))));
    if (!addedControls || pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; refresh(); });
  }).observe(shoppingRoot, {childList:true,subtree:true});
  refresh();
}

class SanjarFilterBrowser extends HTMLElement {
 connectedCallback(){
  this.controller?.abort(); this.controller=new AbortController();
  const buttons=[...this.querySelectorAll('[data-filter-tab]')];
  const panels=[...this.querySelectorAll('[data-filter-panel]')];
  const select=(index)=>{buttons.forEach((b,i)=>{b.setAttribute('aria-selected',String(i===index));b.tabIndex=i===index?0:-1;});panels.forEach((p,i)=>{p.hidden=i!==index;const d=p.querySelector('details');if(d)d.open=true;});};
  buttons.forEach((b,i)=>{b.addEventListener('click',()=>select(i),{signal:this.controller.signal});b.addEventListener('keydown',e=>{let n;if(e.key==='ArrowDown')n=(i+1)%buttons.length;if(e.key==='ArrowUp')n=(i-1+buttons.length)%buttons.length;if(n!==undefined){e.preventDefault();select(n);buttons[n].focus();}},{signal:this.controller.signal});});select(0);
 }
 disconnectedCallback(){this.controller?.abort();}
}
if(!customElements.get('sl-filter-browser'))customElements.define('sl-filter-browser',SanjarFilterBrowser);

class SanjarProductRating extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.addEventListener('click', () => {
      const reviews = document.querySelector('.sl-product-reviews');
      const details = reviews?.querySelector('details');
      if (details) details.open = true;
      reviews?.scrollIntoView({ behavior: behaviour(), block: 'start' });
    }, { signal: this.controller.signal });
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('sl-product-rating')) customElements.define('sl-product-rating', SanjarProductRating);


